import { Profile } from '@/types/Profile'
import { defineStore } from 'pinia'
import useProfileApiInterceptor from '../services/profile-interceptor'
import { LoadingType, useLoadingStore } from './loading'
import { toRaw } from 'vue'
import { getAvatarIcon } from '@/views/profile/avatar-list'

export const useProfilesStore = defineStore('profiles', {
  state: () => ({
    profiles: [] as Profile[],
    activeProfile: undefined as Profile | undefined,
  }),
  getters: {
    getProfiles: (state) => state.profiles,
    getActiveProfile: (state) => state.activeProfile,
    getPrimaryProfile: (state): Profile | undefined =>
      state.profiles.find((p) => p.isPrimary),

    getActiveProfileId: (state): string | undefined =>
      state.activeProfile?.profileId,

    getProfileNameById:
      (state) =>
      (profileId: string | undefined): string => {
        if (!profileId) {
          return ''
        }

        const profile = state.profiles.find((p) => p.profileId === profileId)
        return profile?.name || ''
      },

    getProfileAvatarById:
      (state) =>
      (profileId: string | undefined): string | undefined => {
        if (!profileId) {
          return undefined
        }

        const profile = state.profiles.find((p) => p.profileId === profileId)
        if (!profile) return undefined

        return profile.imageUrl || getAvatarIcon(profile.icon)
      },
  },
  actions: {
    async loadFromDeviceStorage() {
      const profiles = await this.ionicStorage.get('profiles')
      if (profiles) {
        this.profiles = profiles
      }
      await this.loadLocalActiveProfile()
    },

    async initializeActiveProfile() {
      try {
        // Try to get stored active profile ID
        const storedId = await this.ionicStorage.get('activeProfileId')

        // If exists in storage, set it
        if (storedId) {
          await this.setActiveProfile(storedId)
          return
        }

        // Fallback to primary profile
        const primaryId = this.getPrimaryProfile?.profileId
        if (primaryId) {
          await this.setActiveProfile(primaryId)
          return
        }

        // Final fallback to first profile if exists
        if (this.profiles.length > 0) {
          await this.setActiveProfile(this.profiles[0].profileId)
        }
      } catch (error) {
        console.error('Failed to initialize active profile:', error)
        throw error
      }
    },

    async setActiveProfile(profileId: string) {
      const profile = this.profiles.find((p) => p.profileId === profileId)
      if (!profile) {
        throw new Error(`Profile with ID ${profileId} not found`)
      }
      await this.ionicStorage.set('activeProfileId', profileId)
      await this.ionicStorage.set('activeProfile', toRaw(profile))
      this.activeProfile = profile
      await useProfileApiInterceptor(profileId)
    },

    async loadLocalActiveProfile() {
      const storedProfile = await this.ionicStorage.get('activeProfile')

      if (storedProfile) {
        this.activeProfile = storedProfile
      }
    },

    async fetchProfiles() {
      try {
        useLoadingStore().startLoading(LoadingType.fetchProfiles)
        const response = await this.axios.get<Profile[]>('/profiles')
        this.profiles = response.data
        await this.ionicStorage.set('profiles', toRaw(this.profiles))

        // Initialize active profile after fetching
        await this.initializeActiveProfile()
        useLoadingStore().finishLoading(LoadingType.fetchProfiles)
      } catch (error) {
        console.error('Failed to fetch profiles:', error)
        throw error
      }
    },

    async addProfile(profile: Partial<Profile>) {
      try {
        const response = await this.axios.post<Profile>('/profiles', profile)
        this.profiles.push(response.data)
        return response.data
      } catch (error) {
        console.error('Failed to add profile:', error)
        throw error
      }
    },
    async updateProfile(profileId: string, profile: Profile) {
      try {
        const response = await this.axios.put<Profile>(
          `/profiles/${profileId}`,
          profile,
        )
        const profileIndex = this.profiles.findIndex(
          (p) => p.profileId === profileId,
        )
        this.profiles[profileIndex] = response.data
        if (this.activeProfile?.profileId === profileId) {
          this.setActiveProfile(profileId)
        }
        return response.data
      } catch (error) {
        console.error('Failed to update profile:', error)
        throw error
      }
    },
    async deleteProfile(profileId: string) {
      try {
        await this.axios.delete(`/profiles/${profileId}`)
        this.profiles = this.profiles.filter((p) => p.profileId !== profileId)
        if (this.getPrimaryProfile?.profileId) {
          await this.setActiveProfile(this.getPrimaryProfile?.profileId)
        }
      } catch (error) {
        console.error('Failed to delete profile:', error)
        throw error
      }
    },
  },
})
